import { useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

// Portfolio images
import portfolio1 from "@/assets/portfolio/portfolio-1.png";
import portfolio2 from "@/assets/portfolio/portfolio-2.png";
import portfolio3 from "@/assets/portfolio/portfolio-3.png";

import portfolio5 from "@/assets/portfolio/portfolio-5.png";
import portfolio6 from "@/assets/portfolio/portfolio-6.webp";
import portfolio7 from "@/assets/portfolio/portfolio-7.webp";
import portfolio8 from "@/assets/portfolio/portfolio-8.png";
import portfolio9 from "@/assets/portfolio/portfolio-9.png";
import portfolio10 from "@/assets/portfolio/portfolio-10.webp";

// Service image (remplace l'ancienne réalisation "frelons")
import cardNettoyageHauteur from "@/assets/card-nettoyage-hauteur.webp";

interface PortfolioItem {
  id: number;
  src: string;
  title: string;
  category: string;
}

const portfolioItems: PortfolioItem[] = [
  { id: 1, src: portfolio1, title: "Nettoyage panneaux solaires", category: "Nettoyage" },
  { id: 2, src: portfolio2, title: "Démoussage toiture tuiles", category: "Démoussage" },
  { id: 3, src: portfolio3, title: "Radiocommande DJI professionnelle", category: "Équipement" },
  { id: 4, src: cardNettoyageHauteur, title: "Nettoyage en hauteur de façade", category: "Nettoyage" },
  { id: 5, src: portfolio5, title: "Démoussage en action", category: "Démoussage" },
  { id: 6, src: portfolio6, title: "Inspection toiture drone", category: "Inspection" },
  { id: 7, src: portfolio7, title: "Thermographie photovoltaïque", category: "Thermographie" },
  { id: 8, src: portfolio8, title: "Drone professionnel", category: "Équipement" },
  { id: 9, src: portfolio9, title: "Traitement anti-mousse", category: "Démoussage" },
  { id: 10, src: portfolio10, title: "Résultat démoussage", category: "Démoussage" },
];

const categories = ["Tous", "Démoussage", "Inspection", "Nettoyage", "Thermographie", "Équipement"];

const PortfolioGallery = () => {
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const filteredItems = activeCategory === "Tous" 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === activeCategory);

  const currentIndex = selectedImage !== null 
    ? filteredItems.findIndex(item => item.id === selectedImage) 
    : -1;

  const handlePrev = () => {
    if (currentIndex > 0) {
      setSelectedImage(filteredItems[currentIndex - 1].id);
    } else {
      setSelectedImage(filteredItems[filteredItems.length - 1].id);
    }
  };

  const handleNext = () => {
    if (currentIndex < filteredItems.length - 1) {
      setSelectedImage(filteredItems[currentIndex + 1].id);
    } else {
      setSelectedImage(filteredItems[0].id);
    }
  };

  const selectedItem = portfolioItems.find(item => item.id === selectedImage);

  return (
    <section className="py-20 bg-gradient-to-b from-transparent via-muted/30 to-transparent">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="section-title">Nos réalisations</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Découvrez nos interventions par drone : démoussage, inspection, nettoyage et thermographie 
            en Seine-Maritime et dans l'Eure.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === category
                  ? "bg-primary text-primary-foreground shadow-[0_0_20px_hsl(var(--primary)/0.4)]"
                  : "glass-card hover:border-primary/50 text-muted-foreground hover:text-foreground"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              onClick={() => setSelectedImage(item.id)}
              className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer glass-card border-2 border-transparent hover:border-primary/50 transition-all duration-300 animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s`, animationFillMode: "both" }}
            >
              <img
                src={item.src}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-foreground text-sm font-medium truncate">{item.title}</p>
                  <p className="text-primary text-xs">{item.category}</p>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="h-12 w-12 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center border border-primary/30">
                    <ZoomIn className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox Modal */}
        <Dialog open={selectedImage !== null} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-5xl p-0 bg-background/95 backdrop-blur-xl border-primary/20">
            <button
              onClick={() => setSelectedImage(null)}
              aria-label="Fermer l'image"
              className="absolute top-4 right-4 z-50 h-10 w-10 rounded-full bg-background/50 backdrop-blur-sm flex items-center justify-center border border-border hover:bg-primary/20 transition-colors"
            >
              <X className="h-5 w-5 text-foreground" />
            </button>

            {selectedItem && (
              <div className="relative">
                <img
                  src={selectedItem.src}
                  alt={selectedItem.title}
                  className="w-full max-h-[80vh] object-contain rounded-lg"
                />
                
                {/* Navigation Arrows */}
                <button
                  onClick={handlePrev}
                  aria-label="Image précédente"
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-background/50 backdrop-blur-sm flex items-center justify-center border border-border hover:bg-primary/20 transition-colors"
                >
                  <ChevronLeft className="h-6 w-6 text-foreground" />
                </button>
                <button
                  onClick={handleNext}
                  aria-label="Image suivante"
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-background/50 backdrop-blur-sm flex items-center justify-center border border-border hover:bg-primary/20 transition-colors"
                >
                  <ChevronRight className="h-6 w-6 text-foreground" />
                </button>

                {/* Caption */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background to-transparent">
                  <h3 className="text-lg font-semibold text-foreground">{selectedItem.title}</h3>
                  <p className="text-primary text-sm">{selectedItem.category}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default PortfolioGallery;
